import { useCallback, useContext, useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExtendedFormDraft,
  ExternalPatientLinkEnhanced,
  FormContextData,
  FormContextMiniForm,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import {
  getLatestVersion,
  isCqcUserSession,
  isDraftRestricted,
  isSoadUserSession,
  isVersionRestricted,
} from "@thalamos/aspire-common";

import type { FormDataChip } from "../helpers/FormRow.js";
import { FormRow } from "../helpers/FormRow.js";
import { WorkItemStatusCard } from "../helpers/WorkItemStatusCard.js";
import type { ViewableForm } from "./pdfViewerBlocks.js";
import { pdfViewerBlocks } from "./pdfViewerBlocks.js";
import { soadAssessmentLogic } from "./soadAssessmentLogic.js";
import { useSoadNotesDrawer } from "./SoadNotesDrawer.js";
import { SoadReportFormCard } from "./SoadReportFormCard.js";
import { api, apiHooks } from "~/api.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  Dropdown,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { TrainingBanner } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { MultiPagePdf } from "~/components/MultiPagePdf.js";
import { LoggedInUserContext } from "~/Contexts.js";
import type { DialogProps } from "~/hooks/dialogs/index.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";
import { ParticipantsList } from "~/pages/FormProgressPage/helpers/ParticipantsList.js";
import { routeFns } from "~/routePaths.js";

/**
 * One version of a form, or whatever the endpoint serves when no version is
 * named. Each version fetches its own PDF so a warning can be attached to the
 * version it describes, and the warning renders before the PDF has loaded.
 */
function FormVersionPdf({
  formContextId,
  formId,
  version,
  restricted,
  labelVersion,
}: {
  formContextId: string;
  formId: string;
  version?: number;
  restricted: boolean;
  labelVersion: boolean;
}) {
  const { t } = useTranslation();
  const [{ data, loading, response }] = apiHooks.soad.getPdf(
    formContextId,
    formId,
    version,
  );

  return (
    <Box sx={{ mb: 2 }}>
      {restricted && (
        <Banner
          bannerType={BannerList.WARNING}
          title={
            labelVersion && version !== undefined
              ? t("pages.soadAssessment.restrictedDisclosure.version", {
                  version,
                })
              : t("pages.soadAssessment.restrictedDisclosure.form")
          }
          body={
            labelVersion && version !== undefined
              ? t("pages.soadAssessment.restrictedDisclosure.versionDetail")
              : t("pages.soadAssessment.restrictedDisclosure.formDetail")
          }
          sx={{ mb: 2 }}
        />
      )}
      {loading || !data || response?.status !== 200 ? (
        <CircularProgress />
      ) : (
        <MultiPagePdf data={data} />
      )}
    </Box>
  );
}

export function FormPdfView({
  formContextId,
  forms,
  onClose,
  canDownload,
}: {
  formContextId: string;
  forms: ViewableForm[];
  onClose: () => void;
  canDownload?: boolean;
}) {
  const [formIndex, setFormIndex] = useState<number>(0);
  const { t } = useTranslation();
  const userContext = useContext(LoggedInUserContext);

  const firstForm = forms[0];
  if (firstForm === undefined) {
    throw new Error("FormPdfView opened with no forms to show");
  }
  // The Previous/Next buttons keep the index in range; a list that shrinks
  // while the dialog is open falls back to the first form rather than nothing.
  const form = forms[formIndex] ?? firstForm;

  const blocks = pdfViewerBlocks(form, isSoadUserSession(userContext?.user));

  // A form that has only ever had one version has nothing to be distinguished
  // from, so its banner reads as a statement about the form.
  const labelVersion = form.latestVersion > 1;

  return (
    <PopupDialog open={true} onClose={onClose}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        edge="end"
        sx={{
          position: "absolute",
          right: 15,
          top: 8,
        }}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <TrainingBanner />
        {forms.length > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Button
              label="Previous"
              disabled={formIndex === 0}
              onClick={() => {
                setFormIndex((prev) => prev - 1);
              }}
            />
            <Button
              label="Next"
              disabled={formIndex === forms.length - 1}
              onClick={() => {
                setFormIndex((prev) => prev + 1);
              }}
            />
          </Box>
        )}
        {canDownload && (
          <Box sx={{ mb: 2 }}>
            <Button
              label="Download PDF"
              endIcon={ButtonIcon.download}
              onClick={() => {
                const download = async () => {
                  try {
                    // No version named, so the download stays the whole form as
                    // one document.
                    const result = await api.soad.getPdf(
                      formContextId,
                      form.id,
                    );
                    if (result.status !== 200) {
                      renderErrorToast({
                        message:
                          (result as any)?.data?.reason ??
                          "Failed to download PDF",
                      });
                      return;
                    }
                    triggerDownload(
                      `data:application/pdf;base64,${result.data}`,
                      "form.pdf",
                    );
                  } catch (e) {
                    renderErrorToast({
                      message:
                        (e as any)?.response?.data?.reason ??
                        (e as any).message,
                    });
                  }
                };

                void download();
              }}
            />
          </Box>
        )}
        {blocks.unversioned && (
          <FormVersionPdf
            key={form.id}
            formContextId={formContextId}
            formId={form.id}
            restricted={isDraftRestricted(form)}
            labelVersion={false}
          />
        )}
        {blocks.versions.map((version) => (
          <FormVersionPdf
            key={`${form.id}-${String(version.version)}`}
            formContextId={formContextId}
            formId={form.id}
            version={version.version}
            restricted={isVersionRestricted(form, version)}
            labelVersion={labelVersion}
          />
        ))}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          px: 3,
        }}
      >
        <Button onClick={onClose} variant="outlined" label={t("close")} />
      </DialogActions>
    </PopupDialog>
  );
}

function CertificateFormRow({
  title,
  form,
  onViewPdf,
  viewDisabled,
  downloadDisabled,
  onDownloadPdf,
  onUploadToRio,
  uploadToRioLabel,
  restrictedDisclosure,
}: {
  title: string;
  form: FormContextMiniForm;
  onViewPdf: () => void;
  viewDisabled?: boolean;
  downloadDisabled?: boolean;
  onDownloadPdf?: () => void;
  onUploadToRio?: () => void;
  uploadToRioLabel?: string;
  restrictedDisclosure?: boolean;
}) {
  const { t } = useTranslation();
  const latestVersion = getLatestVersion(form);
  const signedByText =
    latestVersion &&
    latestVersion.signatures.length > 0 &&
    `Signed by ${latestVersion.signatures.map((sig) => sig.userName).join(", ")} `;
  const subHeading = `${signedByText || ""}Last updated: ${dayjs.utc(form.updated).fromNow()}`;

  const formDataChips: (string | FormDataChip)[] = [
    ...(form.latestVersion > 1
      ? [`Form Amended - Version Number ${form.latestVersion}`]
      : []),
    ...(restrictedDisclosure
      ? [
          {
            label: t("pages.soadAssessment.restrictedDisclosure.chip"),
            tooltip: `${t("pages.soadAssessment.restrictedDisclosure.form")} ${t(
              "pages.soadAssessment.restrictedDisclosure.formDetail",
            )}`,
            tone: "warning" as const,
          },
        ]
      : []),
  ];

  const menuOptions: MenuOptionsType[] = [
    {
      icon: "view",
      name: "View PDF",
      disabled: viewDisabled ?? false,
      onClick: onViewPdf,
    },
    ...(onDownloadPdf
      ? [
          {
            icon: "download",
            name: "Download PDF",
            disabled: downloadDisabled ?? false,
            onClick: onDownloadPdf,
          } satisfies MenuOptionsType,
        ]
      : []),
    ...(onUploadToRio
      ? [
          {
            icon: "upload",
            name: uploadToRioLabel ?? "Upload to Rio",
            disabled: downloadDisabled ?? false,
            onClick: onUploadToRio,
          } satisfies MenuOptionsType,
        ]
      : []),
  ];

  return (
    <FormRow
      heading={title}
      subHeading={subHeading}
      menuOptions={menuOptions}
      formDataChips={formDataChips}
    />
  );
}

export function SoadAssessmentFormPage({
  reloadFormContext,
  formContext,
  patientTimeline,
  reloadPatientTimeline,
  onReconcileClick,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  patientTimeline: PatientTimelineResponse | null;
  reloadPatientTimeline: () => void;
  onReconcileClick?: () => void;
}) {
  const nhsNumber = patientTimeline?.patient?.nhsNumber;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogs = useDialogs();

  const userContext = useContext(LoggedInUserContext);

  const {
    soadReportForm,
    fullFormName,
    certificates,
    canSave,
    canAmend,
    soadAssessmentComplete,
    canCancelAmend,
    amended,
    amendInProgress,
    canFinaliseForm,
    soadNotes: { draft: soadNotesDraft },
    deletedCertificates,
    canUploadFormToExternalSystem,
  } = soadAssessmentLogic(
    formContext,
    userContext?.user!,
    navigate,
    formContext.activeTeamworkWorkItem !== undefined,
  );

  const isSoad = isSoadUserSession(userContext?.user);
  const isCqc = isCqcUserSession(userContext?.user);
  const isSoadOrCqc = isSoad || isCqc;

  const [soadReportPdfViewOpen, setSoadReportPdfViewOpen] = useState(false);

  const [certificatesPdfViewOpen, setCertificatesPdfViewOpen] = useState(false);
  const [deletedCertificatesPdfViewOpen, setDeletedCertificatesPdfViewOpen] =
    useState(false);
  const [viewingCertificateId, setViewingCertificateId] = useState<
    string | null
  >(null);
  const viewingCertificate = certificates.forms.find(
    (c) => c.form.id === viewingCertificateId,
  )?.form;

  const [certificateLoading, setCertificateLoading] = useState(false);

  const onSaveSoadNotes = useCallback(
    async (notes: string) => {
      if (!soadNotesDraft) {
        return;
      }
      await api.drafts.updateData(soadNotesDraft.id, {
        notes,
        completionStatus: "completed",
      });
    },
    [soadNotesDraft],
  );

  const soadNotesDrawer = useSoadNotesDrawer({
    initialNotes: soadNotesDraft?.data.notes ?? "",
    onSave: onSaveSoadNotes,
  });

  const [showAmendmentInProgressModal, setShowAmendmentInProgressModal] =
    useState(amendInProgress && !isSoad);
  const [showDeleteCertificateDialog, setShowDeleteCertificateDialog] =
    useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<null | {
    form: FormContextMiniForm;
    title: string;
    state: "in-progress" | "completed" | "not-started" | "parked";
    sectionName:
      | "mha-soad-t2"
      | "mha-soad-t3"
      | "mha-soad-t4"
      | "mha-soad-t5"
      | "mha-soad-t6"
      | "mha-soad-cto11"
      | "mha-soad-nocert";
    draft: ExtendedFormDraft | undefined;
    onClick: () => Promise<void>;
  }>(null);

  const certificateIds = certificates.forms.map((f) => f.form.formTemplate.id);

  const selectedValue = [...certificateIds];

  const noCertAddedOrSelected = certificateIds.includes("mha-soad-nocert");

  const anyStatCertAdded =
    certificateIds.length > 0 && !certificateIds.includes("mha-soad-nocert");

  const dropdownValues = [
    {
      value: "mha-soad-t2",
      label: t("pages.soadAssessment.shortCertificateTitles.t2"),
    },
    {
      value: "mha-soad-t3",
      label: t("pages.soadAssessment.shortCertificateTitles.t3"),
    },
    {
      value: "mha-soad-t4",
      label: t("pages.soadAssessment.shortCertificateTitles.t4"),
    },
    {
      value: "mha-soad-t5",
      label: t("pages.soadAssessment.shortCertificateTitles.t5"),
    },
    {
      value: "mha-soad-t6",
      label: t("pages.soadAssessment.shortCertificateTitles.t6"),
    },
    {
      value: "mha-soad-cto11",
      label: t("pages.soadAssessment.shortCertificateTitles.cto11"),
    },
    {
      value: "mha-soad-nocert",
      label: t("pages.soadAssessment.shortCertificateTitles.nocert"),
    },
  ].map((item) => ({
    ...item,
    disabled:
      certificateIds.includes(item.value) ||
      (item.value !== "mha-soad-nocert" && noCertAddedOrSelected) ||
      (item.value === "mha-soad-nocert" && anyStatCertAdded),
  }));

  const addCertificate = useCallback(
    (value: string) => {
      const runAsync = async () => {
        try {
          setCertificateLoading(true);

          const response = await api.soad.addStatutoryCertificate(
            formContext.id,
            value as
              | "mha-soad-t2"
              | "mha-soad-t3"
              | "mha-soad-t4"
              | "mha-soad-t5"
              | "mha-soad-t6"
              | "mha-soad-cto11"
              | "mha-soad-nocert",
          );

          if (response.status !== 204) {
            renderErrorToast({
              message: t("pages.soadAssessment.addCertificateError"),
            });
          }
        } catch {
          renderErrorToast({
            message: t("pages.soadAssessment.addCertificateError"),
          });
        } finally {
          setCertificateLoading(false);
          reloadFormContext();
        }
      };

      void runAsync();
    },
    [formContext.id, reloadFormContext, t],
  );

  const providerEmail = formContext.activeTeamworkWorkItem?.otherWorkItems.find(
    (w) => w.team.type !== "soad" && w.team.type !== "cqc",
  )?.team.primaryEmail;

  const fileNameBase = `${dayjs(soadReportForm.form?.updated).format("YYYYMMDD")}${amended ? "Amended" : ""}SOADPatientReports${soadReportForm.form?.versions[0]?.signatures[0]?.userName.replace(/\s+/g, "") ?? ""}${formContext.officialReferenceId ?? ""}${providerEmail ?? ""}`;

  const externalLinks = useExternalPatientLinks({
    patientId: formContext.patientId,
  });

  const uploadToRioExternalLink:
    ExternalPatientLinkEnhanced | null | undefined = useMemo(() => {
    if (!canUploadFormToExternalSystem) {
      return null;
    }

    if (externalLinks.externalPatientLinksLoading) {
      return undefined;
    }

    const candidateLinks = (
      externalLinks.externalPatientLinks?.externalLinks ?? []
    ).filter(
      (externalPatientLink) => externalPatientLink.externalSystemType === "rio",
    );

    // The lowest number is the highest priority (i.e. 1 is higher priority than 2)
    const highestProirityLink = candidateLinks
      .filter((externalPatientLink) => externalPatientLink.canPush)
      .sort(
        (a, b) =>
          a.priorityWithinExternalSystem - b.priorityWithinExternalSystem,
      )?.[0];

    return highestProirityLink ?? null;
  }, [
    canUploadFormToExternalSystem,
    externalLinks.externalPatientLinks?.externalLinks,
    externalLinks.externalPatientLinksLoading,
  ]);

  return (
    <Stack
      data-testid="soad-form-context"
      sx={[
        {
          gap: 1,
        },
        soadNotesDrawer.siblingSx,
      ]}
    >
      <externalLinks.Component />
      <TrainingBanner />
      {patientTimeline && (
        <Box sx={{ mb: 3 }}>
          <PatientBanner
            patient={patientTimeline.patient}
            nhsNumber={nhsNumber ?? undefined}
            disableSticky
            isHorizontalLineHidden
            patientTimeline={patientTimeline}
            reloadPatientTimeline={reloadPatientTimeline}
            isHidden={isSoadOrCqc}
            onReconcileClick={onReconcileClick}
          />
        </Box>
      )}
      <WorkItemStatusCard
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        user={userContext?.user!}
      />
      {amendInProgress && isSoad && (
        <Banner
          bannerType={BannerList.WARNING}
          title={t("pages.soadAssessment.amendmentInProgressMessage")}
          {...(canCancelAmend
            ? {
                rightButtonLabel: t("pages.soadAssessment.cancelAmendment"),
                onRightButton: async () => {
                  await api.soad.cancelAmendment(formContext.id);
                  reloadFormContext();
                },
              }
            : {})}
          sx={{ mb: 2 }}
        />
      )}
      {amendInProgress && !isSoad && (
        <Banner
          bannerType={BannerList.WARNING}
          title={t("pages.soadAssessment.soadMakingAmendment")}
          sx={{ mb: 2 }}
        />
      )}
      {soadReportPdfViewOpen && soadReportForm.form && (
        <FormPdfView
          formContextId={formContext.id}
          forms={[soadReportForm.form]}
          onClose={() => {
            setSoadReportPdfViewOpen(false);
          }}
          canDownload={soadAssessmentComplete}
        />
      )}
      {deletedCertificatesPdfViewOpen && (
        <FormPdfView
          formContextId={formContext.id}
          forms={deletedCertificates}
          onClose={() => {
            setDeletedCertificatesPdfViewOpen(false);
          }}
          canDownload={soadAssessmentComplete}
        />
      )}
      {viewingCertificate && (
        <FormPdfView
          formContextId={formContext.id}
          forms={[viewingCertificate]}
          onClose={() => {
            setViewingCertificateId(null);
          }}
          canDownload={soadAssessmentComplete}
        />
      )}
      {certificatesPdfViewOpen && (
        <FormPdfView
          formContextId={formContext.id}
          forms={certificates.forms.map((f) => f.form)}
          onClose={() => {
            setCertificatesPdfViewOpen(false);
          }}
          canDownload={soadAssessmentComplete}
        />
      )}
      {amended && !isSoad && soadAssessmentComplete && (
        <Banner
          bannerType={BannerList.WARNING}
          title={t("pages.soadAssessment.alreadyAmendedMessage")}
          sx={{ mb: 2 }}
        />
      )}
      {canFinaliseForm && (
        <Banner
          title={"Mark administrative scrutiny for this assessment complete"}
          rightButtonLabel={"Complete"}
          rightButtonTestId="mark-scrutiny-complete-button"
          onRightButton={() => {
            if (userCanEditMhaStatus(userContext?.user)) {
              navigate(
                routeFns.formContextPageEditMhaStatusDialog(
                  formContext.id,
                  formContext.patientId,
                  {
                    itemName: fullFormName,
                  },
                ),
              );
            } else {
              navigate(
                routeFns.formContextPageConfirmFinaliseDialog(
                  formContext.id,
                  formContext.patientId,
                  {
                    itemName: fullFormName,
                  },
                ),
              );
            }
          }}
          bannerType={BannerList.INFO}
        />
      )}
      {canAmend && (
        <Banner
          bannerType={BannerList.WARNING}
          title={t("pages.soadAssessment.amendmentMessage")}
          rightButtonLabel={t("pages.soadAssessment.startAmendment")}
          onRightButton={() => {
            const runAsync = async () => {
              const result = await dialogs.open(AmendConfirmationDialog);
              if (result.confirmed) {
                if (result.reason) {
                  await api.soad.startAmendment(formContext.id, {
                    reason: result.reason,
                    reasonDescription: result.otherReason,
                  });
                }
                reloadFormContext();
              }
            };

            void runAsync();
          }}
          sx={{ mb: 2 }}
        />
      )}
      <Box sx={{ mb: "2rem", mt: "2rem" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <Box sx={{ display: "flex" }}>
              <FormTitle
                useReducedTopPadding
                hasContainerMarginBottom={false}
                subtitleText={t("pages.soadAssessment.heading")}
              />
              {soadReportForm.editable && (
                <Box sx={{ ml: "1rem" }}>
                  <Button
                    endIcon={ButtonIcon.downloadSecondary}
                    disabled={!soadReportForm.canViewPdf}
                    label="View PDF"
                    onClick={() => {
                      setSoadReportPdfViewOpen(true);
                    }}
                  />
                </Box>
              )}
              {!soadReportForm.editable && (
                <Box sx={{ ml: "1rem" }}>
                  <Button
                    endIcon={ButtonIcon.download}
                    disabled={
                      !soadReportForm.canDownloadPdf ||
                      certificates.forms.length === 0 ||
                      !soadReportForm.form
                    }
                    label="Download All"
                    onClick={() => {
                      const downloadAll = async () => {
                        const firstCertificate = certificates.forms[0];
                        if (firstCertificate === undefined) return;
                        const result = await api.forms.getPdf(
                          firstCertificate.form.id,
                          false,
                          [
                            ...certificates.forms
                              .slice(1)
                              .map((f) => f.form.id),
                            soadReportForm.form!.id,
                          ],
                        );
                        if (result.status === 200 && result.data) {
                          const dataUri = `data:application/pdf;base64,${result.data}`;
                          triggerDownload(dataUri, `${fileNameBase}.pdf`);
                          if (
                            isCqc &&
                            formContext.activeTeamworkWorkItem &&
                            formContext.activeTeamworkWorkItem.status !==
                              "completed"
                          ) {
                            await api.work.archive(
                              formContext.activeTeamworkWorkItem.id,
                            );
                          }
                        } else {
                          renderErrorToast({
                            message:
                              (result as any)?.data?.reason ??
                              "Failed to download PDF",
                          });
                        }
                      };
                      void downloadAll();
                    }}
                  />
                </Box>
              )}
              {!soadReportForm.editable && uploadToRioExternalLink && (
                <Box sx={{ ml: "1rem" }}>
                  <Button
                    endIcon={ButtonIcon.rioUpload}
                    disabled={
                      !soadReportForm.canDownloadPdf ||
                      certificates.forms.length === 0 ||
                      !soadReportForm.form
                    }
                    label={t("pages.soadAssessment.uploadAllToRio")}
                    onClick={() => {
                      const uploadAll = async () => {
                        if (!soadReportForm.form) {
                          renderErrorToast({
                            message: t("pages.soadAssessment.srfNotFoundError"),
                          });
                          return;
                        }
                        await externalLinks.uploadFormPdfViaExternalPatientLink(
                          uploadToRioExternalLink,
                          [
                            ...certificates.forms.map((f) => f.form.id),
                            soadReportForm.form.id,
                          ],
                          formContext,
                        );
                      };
                      void uploadAll();
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "2rem",
                justifyContent: {
                  xs: "center",
                  sm: "flex-start",
                },
              }}
            >
              <Button
                label={`Request ID: ${formContext.officialReferenceId} `}
                endIcon={soadAssessmentComplete ? undefined : ButtonIcon.edit}
                disabled={!soadReportForm.editable}
                variant="outlined"
                onClick={() => {
                  const runAsync = async () => {
                    const newRequestId = await dialogs.prompt("", {
                      title: "Update Request ID",
                      okText: "Confirm",
                      cancelText: "Cancel",
                    });

                    if (newRequestId) {
                      await api.forms.updateOfficialReferenceId(
                        formContext.id,
                        newRequestId,
                      );
                    }
                  };

                  void runAsync();
                }}
              />
            </Box>
          </Box>
          {soadReportForm.editable
            ? soadReportForm.sections.map((section) => (
                <SoadReportFormCard
                  key={section.title}
                  state={section.state}
                  title={t(section.title)}
                  onClick={section.onClick}
                  testId={`srf-${section.sectionName}`}
                />
              ))
            : soadReportForm.form && (
                <CertificateFormRow
                  title={t("pages.soadAssessment.soadReport")}
                  form={soadReportForm.form}
                  viewDisabled={!soadReportForm.canViewPdf}
                  onViewPdf={() => setSoadReportPdfViewOpen(true)}
                  downloadDisabled={!soadReportForm.canDownloadPdf}
                  onDownloadPdf={() => {
                    const downloadSrf = async () => {
                      const result = await api.soad.getPdf(
                        formContext.id,
                        soadReportForm.form!.id,
                      );
                      if (result.status === 200) {
                        const dataUri = `data:application/pdf;base64,${result.data}`;
                        triggerDownload(dataUri, `${fileNameBase}_srf.pdf`);
                        if (
                          isCqc &&
                          formContext.activeTeamworkWorkItem &&
                          formContext.activeTeamworkWorkItem.status !==
                            "completed"
                        ) {
                          await api.work.archive(
                            formContext.activeTeamworkWorkItem.id,
                          );
                        }
                      } else {
                        renderErrorToast({
                          message:
                            (result as any)?.data?.reason ??
                            "Failed to download PDF",
                        });
                      }
                    };
                    void downloadSrf();
                  }}
                  onUploadToRio={
                    uploadToRioExternalLink
                      ? () => {
                          const uploadSrf = async () => {
                            if (!soadReportForm.form) {
                              renderErrorToast({
                                message: t(
                                  "pages.soadAssessment.srfNotFoundError",
                                ),
                              });
                              return;
                            }
                            await externalLinks.uploadFormPdfViaExternalPatientLink(
                              uploadToRioExternalLink,
                              [soadReportForm.form.id],
                              formContext,
                            );
                          };
                          void uploadSrf();
                        }
                      : undefined
                  }
                  uploadToRioLabel={t("pages.soadAssessment.uploadToRio")}
                />
              )}
          {soadReportForm.editable && (
            <Box sx={{ display: "flex", mt: "1rem" }}>
              <FormTitle
                useReducedTopPadding
                hasContainerMarginBottom={false}
                subtitleText={t("pages.soadAssessment.certificates.heading")}
              />
              <Box sx={{ ml: "1rem" }}>
                <Button
                  endIcon={ButtonIcon.downloadSecondary}
                  disabled={certificates.forms.length === 0}
                  label={
                    certificates.forms.length > 1 ? "View PDFs" : "View PDF"
                  }
                  onClick={() => {
                    setCertificatesPdfViewOpen(true);
                  }}
                />
              </Box>
            </Box>
          )}
          {soadReportForm.editable &&
            (certificateLoading ? (
              <CircularProgress />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
                data-testid="certificates-dropdown"
              >
                <Box sx={{ width: "100%" }}>
                  <Dropdown
                    selectedValue={selectedValue}
                    label={t("pages.soadAssessment.certificates.prompt")}
                    subtext={t("pages.soadAssessment.certificates.subText")}
                    values={dropdownValues}
                    multiple
                    name={"certificates"}
                    onChange={(e: string[]) => {
                      const newSelections = e.filter(
                        (value) => !certificateIds.includes(value),
                      );
                      for (const value of newSelections) {
                        addCertificate(value);
                      }
                    }}
                  />
                </Box>
              </Box>
            ))}
          {certificates.forms.map((s) =>
            soadReportForm.editable ? (
              <Box
                key={s.title}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <SoadReportFormCard
                  state={s.state}
                  title={t(s.title)}
                  onClick={s.onClick}
                  testId={s.sectionName}
                />
                <IconButton
                  key={s.form.formTemplate.id}
                  onClick={() => {
                    setCertificateToDelete(s);
                    setShowDeleteCertificateDialog(true);
                  }}
                >
                  <DeleteOutlined color="error" />
                </IconButton>
              </Box>
            ) : (
              <CertificateFormRow
                key={s.title}
                title={t(s.title)}
                form={s.form}
                restrictedDisclosure={s.restrictedDisclosure}
                viewDisabled={!soadReportForm.canDownloadPdf}
                downloadDisabled={!soadReportForm.canDownloadPdf}
                onViewPdf={() => setViewingCertificateId(s.form.id!)}
                onDownloadPdf={() => {
                  const downloadCert = async () => {
                    const result = await api.forms.getPdf(s.form.id, false, []);
                    if (result.status === 200 && result.data) {
                      const certKey =
                        s.sectionName.split("-").pop() ?? s.sectionName;
                      const dataUri = `data:application/pdf;base64,${result.data}`;
                      triggerDownload(
                        dataUri,
                        `${fileNameBase}_${certKey}.pdf`,
                      );
                    } else {
                      renderErrorToast({
                        message:
                          (result as any)?.data?.reason ??
                          "Failed to download PDF",
                      });
                    }
                  };
                  void downloadCert();
                }}
                onUploadToRio={
                  uploadToRioExternalLink
                    ? () => {
                        void externalLinks.uploadFormPdfViaExternalPatientLink(
                          uploadToRioExternalLink,
                          [s.form.id],
                          formContext,
                        );
                      }
                    : undefined
                }
                uploadToRioLabel={t("pages.soadAssessment.uploadToRio")}
              />
            ),
          )}
        </Box>
      </Box>
      {soadAssessmentComplete && deletedCertificates.length > 0 && (
        <Box
          sx={{
            mb: "2rem",
            mt: "2rem",
            display: "flex",
            gap: "1rem",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex" }}>
            <FormTitle
              useReducedTopPadding
              hasContainerMarginBottom={false}
              subtitleText={t("pages.soadAssessment.supersededCertificates")}
            />
            <Box sx={{ ml: "1rem" }}>
              <Button
                endIcon={ButtonIcon.downloadSecondary}
                disabled={deletedCertificates.length === 0}
                label={
                  deletedCertificates.length > 1 ? "View PDFs" : "View PDF"
                }
                onClick={async () => {
                  setDeletedCertificatesPdfViewOpen(true);
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      {soadAssessmentComplete && <ParticipantsList formContext={formContext} />}
      <FormFooterSection
        hideSubmit={!formContext.activeTeamworkWorkItem}
        isSticky={true}
        onSave={() => {
          if (canSave) {
            navigate(
              routeFns.soadSubmitReviewPage(
                formContext.id,
                formContext.patientId,
              ),
            );
          } else {
            navigate(
              routeFns.formContextPageSendRequest(
                formContext.id,
                formContext.patientId,
                {},
              ),
            );
          }
        }}
        disableSubmit={!soadAssessmentComplete && !canSave}
        saveLabel={
          soadAssessmentComplete
            ? t("buttonLabels.send")
            : t("buttonLabels.submit")
        }
        discardLabel={t("buttonLabels.activityLog")}
        onCancel={() => {
          navigate(
            routeFns.formContextPageActivityLog(
              formContext.id,
              formContext.patientId,
            ),
          );
        }}
      >
        {soadReportForm.editable && soadNotesDrawer.Component}
      </FormFooterSection>
      {showAmendmentInProgressModal && (
        <PopupDialog
          open={true}
          onClose={() => {
            setShowAmendmentInProgressModal(false);
          }}
          testId="confirmation-dialog"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "1em",
            }}
          >
            <IconButton
              aria-label={"Close"}
              onClick={() => {
                setShowAmendmentInProgressModal(false);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
            <FormTitle
              useReducedTopPadding={true}
              hasContainerMarginBottom={false}
              showBorder={false}
              isAlignedLeft={false}
              titleText={t(
                "pages.soadAssessment.soadMakingAmendmentModalTitle",
              )}
            />
            <Box sx={{ marginLeft: "2em", marginRight: "2em" }}>
              <Banner
                title={t("pages.soadAssessment.soadMakingAmendmentModalBody")}
                bannerType={BannerList.WARNING}
              />
            </Box>
            <Box
              sx={{
                marginTop: "2em",
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                label={t("close")}
                onClick={() => {
                  setShowAmendmentInProgressModal(false);
                }}
              />
            </Box>
          </Box>
        </PopupDialog>
      )}
      {showDeleteCertificateDialog && (
        <PopupDialog
          open={true}
          onClose={() => {
            setCertificateToDelete(null);
            setShowDeleteCertificateDialog(false);
          }}
          testId="confirmation-dialog"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "1em",
            }}
          >
            <IconButton
              aria-label={"Close"}
              onClick={() => {
                setCertificateToDelete(null);
                setShowDeleteCertificateDialog(false);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
            <FormTitle
              useReducedTopPadding={true}
              hasContainerMarginBottom={false}
              showBorder={false}
              isAlignedLeft={false}
              titleText={t("pages.soadAssessment.deleteCertificateTitle")}
            />
            <Box sx={{ marginLeft: "2em", marginRight: "2em" }}>
              <Banner
                title={t("pages.soadAssessment.deleteCertificateMessage", {
                  certificate: t(certificateToDelete!.title),
                })}
                bannerType={BannerList.WARNING}
              />
            </Box>
            <Box
              sx={{
                marginTop: "2em",
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Button
                label="Close"
                testId="close-button"
                onClick={() => {
                  setCertificateToDelete(null);
                  setShowDeleteCertificateDialog(false);
                }}
                color={"inherit"}
              />
              <Button
                label={t("delete")}
                onClick={() => {
                  const handleClick = async () => {
                    try {
                      const removedValue =
                        certificateToDelete!.form.formTemplate.id;
                      const response =
                        await api.soad.deleteStatutoryCertificate(
                          formContext.id,
                          removedValue as
                            | "mha-soad-t2"
                            | "mha-soad-t3"
                            | "mha-soad-t4"
                            | "mha-soad-t5"
                            | "mha-soad-t6"
                            | "mha-soad-cto11"
                            | "mha-soad-nocert",
                        );
                      if (response.status !== 204) {
                        renderErrorToast({
                          message: t(
                            "pages.soadAssessment.deleteCertificateError",
                          ),
                        });
                      }
                      setCertificateToDelete(null);
                      setShowDeleteCertificateDialog(false);
                    } catch {
                      renderErrorToast({
                        message: t(
                          "pages.soadAssessment.deleteCertificateError",
                        ),
                      });
                      setCertificateToDelete(null);
                      setShowDeleteCertificateDialog(false);
                    }
                  };

                  void handleClick();
                }}
              />
            </Box>
          </Box>
        </PopupDialog>
      )}
    </Stack>
  );
}

type AmendConfirmationDialogResult = {
  confirmed: boolean;
  reason?: string;
  otherReason?: string;
};

export function AmendConfirmationDialog({
  open,
  onClose,
}: DialogProps<unknown, AmendConfirmationDialogResult>) {
  const { t } = useTranslation();

  const [reason, setReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  const handleClose = useCallback(() => {
    void onClose({ confirmed: false });
  }, [onClose]);

  const handleContinue = useCallback(() => {
    const selectedOption = amendReasons.find((r) => r.value === reason);
    const reasonLabel = selectedOption?.label ?? reason;

    void onClose({ confirmed: true, reason: reasonLabel, otherReason });
  }, [reason, onClose, otherReason]);

  const amendReasons = [
    {
      value: "patientNameMisspelt",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.patientNameMisspelt",
      ),
    },
    {
      value: "consulteeNameMisspelt",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.consulteeNameMisspelt",
      ),
    },
    {
      value: "incorrectConsultee",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.incorrectConsulteeNamed",
      ),
    },
    {
      value: "incorrectForm",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.incorrectStatutoryFormUsed",
      ),
    },
    {
      value: "incorrectAddress",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.incorrectPatientAddress",
      ),
    },
    {
      value: "missingMedication",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.missingMedication",
      ),
    },
    {
      value: "missingQuantity",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.missingQuantityOfMedication",
      ),
    },
    {
      value: "missingRoute",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.missingRouteOfMedicine",
      ),
    },
    {
      value: "medicationQuery",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.medicationQuery",
      ),
    },
    {
      value: "wrongBnf",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.wrongBnfCodeLimit",
      ),
    },
    {
      value: "other",
      label: t(
        "pages.soadAssessment.amendConfirmationDialog.dropdown.options.other",
      ),
    },
  ];

  const continueDisabled =
    !reason || (reason === "other" && otherReason.trim().length === 0);

  return (
    <PopupDialog open={open} onClose={handleClose}>
      <PopupDialogTitle
        closeDialog={handleClose}
        titleText={t("pages.soadAssessment.amendConfirmationDialog.title")}
      />
      <Stack
        sx={{
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Banner
          bannerType={BannerList.WARNING}
          body={(
            t("pages.soadAssessment.amendConfirmationDialog.body") as string
          ).split("\n")}
        />
        <Box sx={{ width: "100%" }}>
          <Dropdown
            label={t(
              "pages.soadAssessment.amendConfirmationDialog.dropdown.label",
            )}
            name="amendReason"
            values={amendReasons}
            selectedValue={reason}
            onChange={setReason}
            placeholder="Choose a reason"
          />
        </Box>

        {/* Conditionally show text box if "Other" is selected */}
        {reason === "other" && (
          <TextField
            fullWidth
            label="Reason description"
            multiline
            minRows={2}
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
          />
        )}
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignSelf: "normal",
          }}
        >
          <Button
            label={t("buttonLabels.close")}
            variant="outlined"
            onClick={handleClose}
          />
          <Button
            color="primary"
            onClick={handleContinue}
            label={t("buttonLabels.continue")}
            disabled={continueDisabled}
          />
        </DialogActions>
      </Stack>
    </PopupDialog>
  );
}
